/**
 * Global logger initialization - must be imported first before any decorators
 * This file monkey patches console methods before any other code can execute
 */

import { environment } from './environments/environment';

type LogLevel = 'DBUG' | 'INFO' | 'WARN' | 'FAIL' | 'NONE';

// Early log level determination
const LOG_LEVEL_MAP = new Map<string, LogLevel>([
  ['DEBUG', 'DBUG'],
  ['DBUG', 'DBUG'],
  ['INFO', 'INFO'],
  ['WARN', 'WARN'],
  ['ERROR', 'FAIL'],
  ['FAIL', 'FAIL'],
  ['OFF', 'NONE'],
  ['NONE', 'NONE']
]);

const envLogLevel = (environment as any).logLevel;
const mappedLevel = envLogLevel ? LOG_LEVEL_MAP.get(envLogLevel.toUpperCase()) : undefined;
const currentLogLevel = mappedLevel ?? (environment.production ? 'WARN' : 'DBUG');

// Store original console methods
const originalConsole = (() => {
  const original = {
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  };
  (window as any).originalConsole = original; // Expose for potential use
  return original;
})();

// Log level hierarchy for comparison
const LOG_LEVEL_HIERARCHY: Record<LogLevel, number> = {
  'DBUG': 0,
  'INFO': 1,
  'WARN': 2,
  'FAIL': 3,
  'NONE': 4
};

// Helper function to check if we should log
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_HIERARCHY[level] >= LOG_LEVEL_HIERARCHY[currentLogLevel];
}

// Monkey patch console methods - single sink with environment-based filtering and styling
console.debug = (...args: any[]) => {
  if (shouldLog('DBUG')) {
    // First arg should be the formatted message from Logger
    const formattedMessage = args[0] || '';
    originalConsole.debug(`%c${formattedMessage}`, 'color: lime;', ...args.slice(1));
  }
};

console.info = (...args: any[]) => {
  if (shouldLog('INFO')) {
    // First arg should be the formatted message from Logger
    const formattedMessage = args[0] || '';
    originalConsole.info(`%c${formattedMessage}`, 'color: aqua;', ...args.slice(1));
  }
};

console.log = (...args: any[]) => {
  console.info(...args);
};

console.warn = (...args: any[]) => {
  if (shouldLog('WARN')) {
    // First arg should be the formatted message from Logger
    const formattedMessage = args[0] || '';
    originalConsole.warn(formattedMessage, ...args.slice(1));
  }
};

console.error = (...args: any[]) => {
  if (shouldLog('FAIL')) {
    // First arg should be the formatted message from Logger
    const formattedMessage = args[0] || '';
    originalConsole.error(formattedMessage, ...args.slice(1));
  }
};

// Immediate confirmation that monkey patching is active
console.debug('Console monkey patching enabled globally before decorators');

// Export for potential use
export { originalConsole, shouldLog, currentLogLevel };
